import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import API from "../api";

// 🟢 NEW: Haptic Feedback Engine
const vibrate = (pattern) => {
  if (typeof window !== "undefined" && navigator.vibrate) {
    navigator.vibrate(pattern);
  }
};

const SearchBar = () => {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const navigate = useNavigate();
  const searchRef = useRef(null);

  // 🟢 1. DEBOUNCING: Wait 400ms before hitting the server
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (query.length > 1) {
        try {
          // Fetch suggestions (limit to 5 for speed)
          const { data } = await API.get(`/products?search=${query}&limit=5`);
          setSuggestions(data.products || []);
          setShowSuggestions(true);
        } catch (error) {
          console.error("Search Error", error);
        }
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 400); // Wait 400ms

    return () => clearTimeout(delayDebounceFn);
  }, [query]);

  // Handle clicking a suggestion
  const handleSelect = (id) => {
    vibrate(50); // 📳 Firm tap on selection
    navigate(`/shop?search=${query}`); // Or go to specific product page
    setShowSuggestions(false);
  };

  // Handle "Enter" key
  const handleSearch = (e) => {
    if (e.key === "Enter") {
      vibrate(50); // 📳 Firm tap on submit
      navigate(`/shop?search=${query}`);
      setShowSuggestions(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={searchRef} className="relative w-full max-w-md mx-auto">
      {/* Search Input */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search for milk, chips..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleSearch}
          className="w-full pl-10 pr-4 py-2.5 rounded-full border border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-100 outline-none transition-all bg-gray-50 focus:bg-white shadow-sm"
        />
        <span className="absolute left-3.5 top-3 text-gray-400">🔍</span>
        
        {/* Clear Button */}
        {query && (
          <button 
            onClick={() => {
              vibrate(30); // 📳 Light tap for clearing
              setQuery(""); 
              setSuggestions([]);
            }} 
            className="absolute right-3 top-3 text-gray-400 hover:text-red-500"
          >
            ✕
          </button>
        )}
      </div>

      {/* 🟢 2. AUTO-SUGGESTIONS DROPDOWN */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute top-full mt-2 w-full bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2">
          {suggestions.map((p) => (
            <div 
              key={p._id}
              onClick={() => handleSelect(p._id)}
              className="flex items-center gap-3 p-3 hover:bg-green-50 cursor-pointer border-b border-gray-50 last:border-0 transition-colors"
            >
              <img src={p.image} alt="" className="w-10 h-10 rounded-md object-cover bg-gray-200" />
              <div>
                <div className="font-bold text-gray-800 text-sm">{p.name}</div>
                <div className="text-xs text-gray-500">{p.category} • <span className="text-green-600 font-bold">₹{p.price}</span></div>
              </div>
            </div>
          ))}
          <div 
            onClick={() => handleSearch({ key: "Enter" })}
            className="p-3 text-center text-green-600 text-sm font-bold bg-gray-50 hover:bg-gray-100 cursor-pointer"
          >
            View all results for "{query}" ➔
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;